import { useState, useCallback, useMemo } from "react";
import {
  Modal,
  FormLayout,
  TextField,
  Select,
  Text,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { SocialProfile } from "@features/events/types";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { validateSocialMediaUrl } from "@shared/lib";

interface AddSocialProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (profile: SocialProfile) => void;
  editingProfile?: SocialProfile | null;
}

export default function AddSocialProfileModal({
  open,
  onClose,
  onSave,
  editingProfile,
}: AddSocialProfileModalProps) {
  const { t } = useTranslation();
  const { socialProfileOptions } = useEventMetadata();
  const defaultProfile = useMemo<SocialProfile>(
    () => ({
      social: socialProfileOptions[0]?.value || "instagram",
      url: "",
    }),
    [socialProfileOptions]
  );
  const baseProfile = editingProfile ?? defaultProfile;

  const [profileOverride, setProfileOverride] = useState<SocialProfile | null>(
    null
  );
  const profile = profileOverride ?? baseProfile;
  const [errors, setErrors] = useState<{ url?: string }>({});

  const validateUrl = useCallback((url: string, platform: string) => {
    const result = validateSocialMediaUrl(platform, url);
    return result.error || "";
  }, []);

  const handleSave = useCallback(() => {
    const urlError = validateUrl(profile.url, profile.social);

    if (urlError) {
      setErrors({ url: urlError });
      return;
    }

    onSave(profile);
    setProfileOverride(null);
    setErrors({});
    onClose();
  }, [profile, onSave, onClose, validateUrl]);

  const handleClose = useCallback(() => {
    setProfileOverride(null);
    setErrors({});
    onClose();
  }, [onClose]);

  const handleUrlChange = useCallback(
    (value: string) => {
      setProfileOverride((prev) => ({ ...(prev ?? baseProfile), url: value }));
      if (errors.url && value.trim()) {
        setErrors((prev) => ({ ...prev, url: undefined }));
      }
    },
    [baseProfile, errors.url]
  );

  const handlePlatformChange = useCallback((value: string) => {
    setProfileOverride((prev) => ({
      ...(prev ?? baseProfile),
      social: value,
    }));
  }, [baseProfile]);

  const isEditing = Boolean(editingProfile);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        isEditing
          ? t("instructorDetails.socialProfiles.modal.editTitle")
          : t("instructorDetails.socialProfiles.modal.title")
      }
      primaryAction={{
        content: isEditing
          ? t("common.update")
          : t("instructorDetails.socialProfiles.modal.addButton"),
        onAction: handleSave,
        disabled: !profile.url.trim(),
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <Text as="p" variant="bodyMd" tone="subdued">
            {t("instructorDetails.socialProfiles.modal.description")}
          </Text>

          <Box maxWidth="200px">
            <Select
              label={t("instructorDetails.socialProfiles.modal.platformLabel")}
              options={socialProfileOptions}
              value={profile.social}
              onChange={handlePlatformChange}
            />
          </Box>

          <TextField
            label={t("instructorDetails.socialProfiles.modal.urlLabel")}
            type="url"
            value={profile.url}
            onChange={handleUrlChange}
            error={errors.url}
            placeholder={t("common.placeholders.url")}
            autoComplete="url"
            helpText={t("instructorDetails.socialProfiles.modal.urlHelpText")}
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}
